<template>
    <div class="w-full">
        <div v-if="isProcessing" class="rounded-lg border border-blue-200 bg-blue-50/50 overflow-hidden shadow-sm">
            <div class="px-4 py-3 flex items-center gap-2 text-sm text-blue-600">
                <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 animate-spin" />
                <span>Preparing campaign changes...</span>
            </div>
        </div>

        <div v-else-if="hasError" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <div class="flex items-center gap-2 text-sm text-red-700">
                <UIcon name="i-heroicons-x-circle" class="w-4 h-4" />
                <span>{{ part.errorText || 'Failed to generate modification plan' }}</span>
            </div>
        </div>

        <div v-else-if="plan" class="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
            <div class="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <div class="flex items-center gap-2">
                    <UIcon name="i-heroicons-pencil-square" class="w-4.5 h-4.5 text-blue-600 flex-shrink-0" />
                    <h3 class="font-semibold text-sm text-slate-900">{{ plan.campaignName }}</h3>
                </div>
                <p class="text-xs text-slate-500 mt-0.5 ml-6.5">{{ compact(plan.summary, 150) }}</p>
            </div>

            <div class="divide-y divide-slate-100">
                <div v-for="change in visibleChanges" :key="change.label" class="px-4 py-3">
                    <div class="flex items-start gap-2">
                        <UIcon :name="iconFor(change.section)" class="w-4 h-4 flex-shrink-0 mt-0.5" :class="iconClassFor(change.section)" />
                        <div class="flex-1 min-w-0 space-y-1.5">
                            <h4 class="font-semibold text-xs text-slate-900 uppercase tracking-wide">{{ change.label }}</h4>
                            <div class="text-sm leading-5 space-y-1">
                                <p v-if="change.before" class="text-slate-400 line-through decoration-slate-400 decoration-1">
                                    {{ compact(change.before, 220) }}
                                </p>
                                <p class="text-slate-800">
                                    {{ change.section === 'targeting' ? change.after : compact(change.after, 260) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="plan.exampleOutreach" class="px-4 py-3">
                    <div class="flex items-start gap-2">
                        <UIcon name="i-heroicons-envelope" class="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <div class="flex-1 min-w-0 space-y-2">
                            <h4 class="font-semibold text-xs text-slate-900 uppercase tracking-wide">{{ plan.exampleOutreach.receiptId ? 'Generated contact preview' : 'Unverified historical example' }}</h4>
                            <p v-if="plan.exampleOutreach.receiptId" class="text-xs text-slate-500">Exact saved preview. Generated claims still need review.</p>
                            <div class="rounded-md border border-slate-200 bg-slate-50/80 overflow-hidden">
                                <div v-if="plan.exampleOutreach.beforeSubject || plan.exampleOutreach.afterSubject" class="px-3 py-1.5 border-b border-slate-200 bg-white space-y-0.5">
                                    <div v-if="plan.exampleOutreach.beforeSubject" class="text-sm text-slate-400 line-through decoration-slate-400">
                                        <span class="text-[10px] uppercase tracking-wider font-medium mr-1.5 no-underline">Subject:</span>{{ compact(plan.exampleOutreach.beforeSubject, 100) }}
                                    </div>
                                    <div v-if="plan.exampleOutreach.afterSubject" class="text-sm text-slate-800 font-medium">
                                        <span class="text-[10px] text-slate-400 uppercase tracking-wider font-medium mr-1.5">Subject:</span>{{ plan.exampleOutreach.afterSubject }}
                                    </div>
                                </div>
                                <div class="px-3 py-2 text-sm leading-relaxed space-y-2">
                                    <p v-if="plan.exampleOutreach.beforeBody" class="text-slate-400 whitespace-pre-wrap line-through decoration-slate-400">
                                        {{ compact(plan.exampleOutreach.beforeBody, 360) }}
                                    </p>
                                    <p v-if="plan.exampleOutreach.afterBody" class="text-slate-700 whitespace-pre-wrap">
                                        {{ plan.exampleOutreach.afterBody }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="plan.footer" class="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 text-xs text-slate-500">
                <p>{{ plan.footer }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
type ChangeSection = 'targeting' | 'angle' | 'personalization' | 'sequence' | 'outreach' | 'other';

interface ModificationChange {
    section?: ChangeSection;
    label: string;
    before?: string;
    after: string;
}

interface ModificationPlan {
    campaignName: string;
    summary: string;
    changes: ModificationChange[];
    exampleOutreach?: {
        receiptId?: string;
        beforeSubject?: string;
        afterSubject?: string;
        beforeBody?: string;
        afterBody?: string;
    };
    footer?: string;
}

const props = defineProps<{
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

const isProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const hasError = computed(() =>
    props.part.state === 'output-error' ||
    (props.part.errorText !== undefined && props.part.errorText !== '')
);

const plan = computed<ModificationPlan | null>(() => {
    const data = props.part.output as Partial<ModificationPlan> | undefined;
    if (!data?.campaignName || !Array.isArray(data.changes)) return null;
    return {
        campaignName: data.campaignName,
        summary: data.summary || 'Review the proposed campaign changes.',
        changes: data.changes.filter(isModificationChange),
        exampleOutreach: data.exampleOutreach,
        footer: data.footer,
    };
});

const visibleChanges = computed(() => plan.value?.changes.slice(0, 5) ?? []);

function isModificationChange(value: unknown): value is ModificationChange {
    if (!value || typeof value !== 'object') return false;
    const change = value as Partial<ModificationChange>;
    return typeof change.label === 'string' && typeof change.after === 'string';
}

function compact(text: string | undefined, maxLength: number): string {
    const normalized = (text || '').trim().replace(/[ \t]+/g, ' ');
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

function iconFor(section?: ChangeSection): string {
    switch (section) {
        case 'targeting': return 'i-heroicons-user-group';
        case 'angle': return 'i-heroicons-light-bulb';
        case 'personalization': return 'i-heroicons-sparkles';
        case 'sequence': return 'i-heroicons-numbered-list';
        case 'outreach': return 'i-heroicons-envelope';
        default: return 'i-heroicons-adjustments-horizontal';
    }
}

function iconClassFor(section?: ChangeSection): string {
    switch (section) {
        case 'targeting': return 'text-blue-600';
        case 'angle': return 'text-amber-500';
        case 'personalization': return 'text-violet-500';
        case 'sequence': return 'text-cyan-600';
        case 'outreach': return 'text-emerald-500';
        default: return 'text-slate-500';
    }
}
</script>
